// src/ts/app/src/theme/tokens.ts
// Foundation values were copied verbatim from theme.tsx.
// SP0 (Samply redesign) expands this with the full redesign palette — values
// copied verbatim from docs/redesign/ommuse-theme.css. Existing keys are kept so
// nothing breaks; new keys are additive. The visual rollout is opt-in: the
// gradient/glow/radius tokens are consumed via `sx`, while font + base surface
// land globally through the MUI theme (see theme.tsx).

export const tokens = {
  color: {
    /** Page/component backgrounds */
    surface: {
      /** Main app background — APP_BACKGROUND_COLOR / --bg */
      base: "#0C111D",
      /** Main app background at 95% alpha, for frosted sticky chrome — --bg-glass.
       *  Prefer the `bg-bg-glass` Tailwind utility; `bg-bg/95` emits no CSS. */
      baseGlass: "rgba(12, 17, 29, 0.95)",
      /** InitialSettings dark background (legacy) */
      raisedDark: "#151718",
      /** Navbar + footer background — NAVBAR_AND_FOOTER_BACKGROUND_COLOR */
      navbarFooter: "black",
      // --- redesign surface ramp (ommuse-theme.css) ---
      /** Lifted panels — --surface */
      raised: "#151718",
      /** Chips / inputs — --surface-2 */
      raised2: "#1b1e26",
      /** Hover / active — --surface-hi */
      raisedHi: "#21242c",
      /** Sidebar / footer — --black */
      black: "#000000",
    },
    text: {
      /** Body text — TEXT_COLOR (canonical --text) */
      primary: "#C2C5C9",
      /** Skip-button muted text — SKIP_BUTTON_TEXT_COLOR */
      muted: "#889096",
      /** SVG icon fill — SVG_ICON_COLOR */
      icon: "#FFFFFF",
      /** Navbar/footer text — NAVBAR_FOOTER_TEXT_COLOR */
      navbarFooter: "white",
      /** Ink on a cyan-filled button — BUTTON_TEXT_COLOR / --cyan-ink.
       *  Light text on a dark surface uses `navbarFooter` or `primary` instead. */
      button: "#04222a",
      // --- redesign text ramp (ommuse-theme.css) ---
      /** Primary body — --text */
      body: "#C2C5C9",
      /** Headings — --white */
      heading: "#FFFFFF",
      /** Secondary — --muted */
      secondary: "#8a929a",
      /** Tertiary / placeholder — --faint */
      faint: "#5b626a",
    },
    /** Dividers / card borders — BORDER_COLOR / --border */
    border: "#26292B",
    /** Soft hairline divider — --border-soft */
    borderSoft: "#1c1f24",
    /** Brand accent / primary interactive colour — APP_COLOR / --cyan */
    accent: "#27B6CB",
    /** Hover state of accent — APP_HOVER_COLOR / --cyan-hover */
    accentHover: "#1A8899",
    /** Text on cyan fills — --cyan-ink */
    accentInk: "#04222a",
    /** Bright top of the luminous accent — --accent-1 */
    accent1: "#3DD3E6",
    /** Deep bottom of the luminous accent — --accent-2 */
    accent2: "#1B90AB",
    /** Soft accent tint (badges/fills) — --accent-soft */
    accentSoft: "rgba(39,182,203,.14)",
    /** Cyan hairline on soft fills — --accent-line */
    accentLine: "rgba(39,182,203,.30)",
    /** Cyan soft fill, hover step — --accent-soft-2 */
    accentSoft2: "rgba(39,182,203,.24)",
    /** Secondary ambient accent, used sparingly — --violet */
    violet: "#8A6CF0",
    /** Active navbar item — NAVBAR_ACTIVE_TEXT_COLOR (equals APP_COLOR) */
    navbarActive: "#27B6CB",
    /** Warning / near-limit amber — guest quota "running low", trial-warn banner */
    warn: "#E3A549",
    /** Text on solid warn fills */
    warnInk: "#2a1d07",
    /** Danger / at-limit red — guest quota full, trial-expire banner */
    danger: "#E5575C",
    /** Text on solid danger fills */
    dangerInk: "#FFFFFF",
    /** Danger tint for hover rows — --danger-soft */
    dangerSoft: "rgba(229,87,92,.12)",
  },
  /** Luminous gradients (opt-in via sx) */
  gradient: {
    /** Primary CTA / active-bit fill — --accent-grad */
    accent: "linear-gradient(150deg, #3DD3E6, #27B6CB 50%, #1B90AB)",
    /** Selected segment in a mode-toggle — --seg-on. Deliberately neutral;
     *  the accent gradient is reserved for filter chips and CTAs.
     *  Prefer the `.seg-on` Tailwind component class over consuming this in sx. */
    segOn: "linear-gradient(180deg, #242835, #191c24)",
  },
  /** Elevation / glow shadows (opt-in via sx) */
  shadow: {
    /** Accent glow — --glow */
    glow: "0 8px 24px -7px rgba(39,182,203,.55), 0 2px 6px -1px rgba(39,182,203,.28)",
    /** Stronger accent glow — --glow-strong */
    glowStrong:
      "0 12px 32px -7px rgba(39,182,203,.7), 0 0 0 1px rgba(61,211,230,.25) inset",
    /** Lit-from-above hairline — --edge-hi */
    edgeHi: "inset 0 1px 0 rgba(255,255,255,.055)",
  },
  /** Spacing function: mirrors MUI's default 8 px base.
   *  Usage: tokens.space(2) === 16 */
  space: (n: number) => n * 8,
  /** Corner radii — --radius-sm / --radius */
  radius: { sm: 10, md: 12 },
  typography: {
    /** Base font family — redesign default is Sora, Argumentum kept as fallback */
    family: "Sora, Inter, system-ui, -apple-system, sans-serif",
  },
} as const;

export type Tokens = typeof tokens;

// Samply-derived share-surface tokens (2026-07-19). Flat named exports (rather
// than nested under `tokens`) so consumers can import them directly, mirroring
// the CSS-var names 1:1 — see docs/redesign/ommuse-theme.css / theme/tokens.css.
export const sheet = "var(--sheet)";
export const wash = "var(--wash)";
export const hairline = "var(--hairline)";
export const fill1 = "var(--fill-1)";
export const fill2 = "var(--fill-2)";
export const fill3 = "var(--fill-3)";
export const textHi = "var(--text-hi)";
export const textDim = "var(--text-dim)";
